import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search as SearchIcon } from "lucide-react";
import { api } from "@/lib/api";
import ContentCard from "@/components/ContentCard";
import { COUNTRY_LIST, LANGUAGES } from "@/lib/constants";

export default function Search() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [country, setCountry] = useState("");
  const [language, setLanguage] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const run = async () => {
    setLoading(true); setSearched(true);
    try {
      const { data } = await api.get("/content", { params: { q, country, language } });
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    const query = params.get("q");
    if (query) { setQ(query); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    if (params.get("q")) run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = (e) => {
    e.preventDefault();
    setParams(q ? { q } : {});
    run();
  };

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-8">
      <h1 className="font-display font-black text-3xl sm:text-4xl mb-6">Search</h1>
      <form onSubmit={submit} className="glass rounded-2xl p-4 flex flex-col md:flex-row gap-3 mb-8">
        <div className="relative flex-1">
          <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
          <input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} autoFocus
            placeholder="Search by title, creator, description…"
            className="w-full h-12 pl-12 pr-4 rounded-lg bg-white/5 border border-white/10 focus:border-[#E50914]/60 focus:outline-none" />
        </div>
        <select value={country} onChange={(e) => setCountry(e.target.value)} data-testid="search-country"
          className="h-12 px-4 rounded-lg bg-white/5 border border-white/10 focus:outline-none text-sm">
          <option value="" className="bg-[#1A1724]">All countries</option>
          {COUNTRY_LIST.map((c) => <option key={c.code} value={c.code} className="bg-[#1A1724]">{c.flag} {c.name}</option>)}
        </select>
        <select value={language} onChange={(e) => setLanguage(e.target.value)} data-testid="search-language"
          className="h-12 px-4 rounded-lg bg-white/5 border border-white/10 focus:outline-none text-sm">
          <option value="" className="bg-[#1A1724]">All languages</option>
          {LANGUAGES.map((l) => <option key={l} value={l} className="bg-[#1A1724]">{l}</option>)}
        </select>
        <button data-testid="search-submit" className="h-12 px-8 rounded-lg bg-[#E50914] hover:bg-[#FF2A3B] font-semibold transition-colors">
          Search
        </button>
      </form>

      {loading ? (
        <div className="min-h-[30vh] flex items-center justify-center">
          <div className="w-10 h-10 border-4 border-[#E50914] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : searched ? (
        items.length ? (
          <>
            <p className="text-white/50 text-sm mb-4">{items.length} results</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-testid="search-results">
              {items.map((it, i) => <ContentCard key={it.id} item={it} index={i} />)}
            </div>
          </>
        ) : (
          <p className="text-white/40 text-center py-16 font-display text-lg">No results found. Try another search.</p>
        )
      ) : (
        <p className="text-white/40 text-center py-16 font-display text-lg">Start typing to discover African stories.</p>
      )}
    </div>
  );
}
